import { createAsyncThunk } from '@reduxjs/toolkit'
import apiRequest from "../../lib/http"
import { businessTimeZone } from "../../utils/date"
import { normalizeError } from "../../utils/errorHelpers"

export const fetchAffiliates = createAsyncThunk('affiliates/fetchAffiliates', async ({
                                                                                       limit,
                                                                                       offset,
                                                                                       searchQuery,
                                                                                       filters,
                                                                                       sortField = '',
                                                                                       sortOrder = '',
                                                                                       affiliateStatus,
                                                                                     }, { rejectWithValue }) => {
  try {
    let result = filters && encodeURI(JSON.stringify(filters))

    const response = await apiRequest.get('/affiliates', {
      params: {
        limit,
        offset,
        searchQuery,
        filters: result === "%22%22" ? "" : result,
        sortField,
        sortOrder,
        timezone: businessTimeZone,
        ...(affiliateStatus ? { affiliateStatus } : {}),
      }
    })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch affiliates'
    return rejectWithValue(errorMessage)
  }
})

export const fetchAllAffiliatesDropdown = createAsyncThunk('affiliates/fetchAllAffiliatesDropdown', async ({ affiliatesOnly = false }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/affiliates/dropdown/info?affiliatesOnly=${affiliatesOnly}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch affiliates'
    return rejectWithValue(errorMessage)
  }
})

export const fetchAllAffiliatesDropdownByOwner = createAsyncThunk('affiliates/fetchAllAffiliatesDropdownByOwner', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/affiliates/dropdown/info/owner`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch affiliates'
    return rejectWithValue(errorMessage)
  }
})

export const createNewAffiliate = createAsyncThunk('affiliates/createAffiliate', async ({
                                                                                          userData,
                                                                                        }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/affiliates`, userData)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to create affiliate'
    return rejectWithValue(errorMessage)
  }
})

export const updateAffiliate = createAsyncThunk('affiliates/updateAffiliate', async ({
                                                                                       userData, userId
                                                                                     }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/affiliates/${userId}`, userData)
    return response.data
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})

// Read-only: what saving these contact groups would do to the affiliate's files. POST, with the same
// body shape as the save, so the server's access guard authorizes exactly the groups being changed.
export const previewAffiliateGroupChange = createAsyncThunk('affiliates/previewAffiliateGroupChange', async ({
  userId, contactGroups
}, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/affiliates/${userId}/group-change-preview`, { contactGroups })
    return response.data
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})

export const getAffiliateById = createAsyncThunk('affiliates/getAffiliateById', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/affiliates/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch affiliate'
    return rejectWithValue(errorMessage)
  }
})

export const checkIsAffiliateContactGroupOwner = createAsyncThunk('affiliates/checkIsAffiliateContactGroupOwner', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/affiliates/${userId}/owner`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to fetch affiliate'
    return rejectWithValue(errorMessage)
  }
})

// Takes a userId, or { userId, reassignTo } to hand the agent's files to another agent first (WS #2303654).
// Rejects with { message, code } so the caller can tell "agent still holds files" (agentHasFiles) apart.
export const removeAffiliate = createAsyncThunk('affiliates/removeAffiliate', async (arg, { rejectWithValue }) => {
  const { userId, reassignTo } = typeof arg === 'object' && arg !== null ? arg : { userId: arg }
  try {
    const query = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''
    const response = await apiRequest.delete(`/affiliates/${userId}${query}`)
    return response.data
  } catch (error) {
    const firstError = error.response?.data?.error?.errors?.[0]
    return rejectWithValue({ message: firstError?.message || 'Failed to delete affiliate', code: firstError?.code })
  }
})

export const changeAffiliateStatus = createAsyncThunk('affiliates/changeAffiliateStatus', async ({ userId, status }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.patch(`/affiliates/${userId}/status`, { status })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to change affiliate status'
    return rejectWithValue(errorMessage)
  }
})

export const resendAffiliateInvite = createAsyncThunk('affiliates/resendAffiliateInvite', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/affiliates/${userId}/resend-invite`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to resend invitation'
    return rejectWithValue(errorMessage)
  }
})

export const changeAffiliateAccess = createAsyncThunk('affiliates/changeAffiliateAccess', async ({ userId, accessLevel }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.patch(`/affiliates/${userId}/access`, { accessLevel })
    return response.data
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})

export const sendAffiliatePasswordReset = createAsyncThunk('affiliates/sendAffiliatePasswordReset', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.post(`/affiliates/${userId}/send-password-reset`)
    return response.data
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})

export const grantOfficeOwnership = createAsyncThunk('affiliates/grantOfficeOwnership', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.patch(`/affiliates/${userId}/office-owner`)
    return response.data
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})

export const fetchTeamOffices = createAsyncThunk('affiliates/fetchTeamOffices', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get('/affiliates/team/offices')
    return response.data
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})
